import { boxLinePeaks } from '../../utils/boxLinePoints';
// frontend/src/components/tables/BoxLinesTable.js
import { useState, useEffect } from 'react';
import axios from 'axios';
import DataTable from '../common/DataTable';
import useManualStrategyActions from '../../hooks/useManualStrategyActions';
import StrategyControls, { strategyStatus } from '../common/StrategyControls';
import { boxLineColor } from '../../utils/boxLineStyle';

export default function BoxLinesTable({
  daysDelta,
  control,
  onStrategyChanged,
  refreshKey = 0,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    canManage,
    isManual,
    pendingId,
    message,
    errorMessage,
    handleStrategy,
  } = useManualStrategyActions(control, onStrategyChanged);

  function getKSTIsoString(date) {
    const formatted = new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Asia/Seoul',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date); // "2025-04-17 07:00:00"
    return formatted.replace(' ', 'T') + '+09:00';
  }

  useEffect(() => {
    const controller = new AbortController();
    let fetching = false;
    setLoading(true);
    setError(null);
    const reload = async () => {
      if (fetching) return;
      fetching = true;
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/boxlines/get_box_lines_table`,
          {
            params: { days: daysDelta, include_strategy: true },
            signal: controller.signal,
          },
        );
        if (!controller.signal.aborted) {
          setRows(res.data);
          setError(null);
        }
      } catch (err) {
        if (!controller.signal.aborted) setError(errorMessage(err));
      } finally {
        fetching = false;
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    reload();
    const timer = setInterval(reload, 5000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [daysDelta, refreshKey]);

  // 공통 컬럼 정의
  const columns = [
    {
      header: '출처',
      render: (r) =>
        r.calculation_kind === 'replay'
          ? `dev 재계산 · ${r.calculation_run_id?.slice(0, 8) || ''}${r.managed_boxline_id ? ` · 운용 #${r.managed_boxline_id}` : ' · 조회용'}`
          : '운용',
    },
    {
      header: '상/하',
      accessor: 'is_top',
      render: (r) => (
        <span
          style={{
            color: boxLineColor(
              r,
              r.is_top
                ? r.n_peaks === 2
                  ? 'pink'
                  : 'red'
                : r.n_peaks === 2
                  ? 'skyblue'
                  : 'blue',
            ),
          }}
        >
          {r.is_top ? '상단' : '하단'}
        </span>
      ),
    },
    {
      header: '봉우리 수',
      accessor: 'n_peaks',
      render: (r) => r.n_peaks || '수동',
    },
    ...Array.from({ length: 5 }, (_, index) => ({
      header: `봉우리${index + 1}`,
      accessor: `peak_${index + 1}`,
      render: (r) => {
        const peak = boxLinePeaks(r)[index];
        return peak
          ? getKSTIsoString(new Date(peak.timestamp_korean)).substring(11, 19)
          : '—';
      },
    })),
    {
      header: '지수',
      accessor: 'close_box',
      render: (r) => r.close_box.toFixed(2),
    },
    {
      header: '전략 상태',
      render: (r) =>
        r.calculation_kind === 'replay' && !r.strategy
          ? '조회용 · 전략 없음'
          : strategyStatus(r.strategy, isManual),
    },
    ...(canManage
      ? [
          {
            header: '진입선 관리',
            render: (r) => (
              <StrategyControls
                row={r}
                control={control}
                pendingId={pendingId}
                onAction={handleStrategy}
              />
            ),
          },
        ]
      : []),
  ];

  if (loading)
    return <p className='text-center text-neutral-500'>Loading...</p>;
  if (error) return <p className='text-center text-red-600'>Error: {error}</p>;

  const tops = rows.filter((r) => r.is_top);
  const bottoms = rows.filter((r) => !r.is_top);

  return (
    <div className='w-full max-w-[1600px] mx-auto px-4'>
      <div className='grid grid-cols-1 gap-6 w-full min-w-0'>
        <div className='w-full min-w-0'>
          <DataTable title='전체 박스라인' rows={rows} columns={columns} />
        </div>

        <div className='w-full min-w-0'>
          <DataTable title='상단선' rows={tops} columns={columns} />
        </div>

        <div className='w-full min-w-0'>
          <DataTable title='하단선' rows={bottoms} columns={columns} />
        </div>
      </div>
    </div>
  );
}
